import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { useMasterDataStatus } from '../hooks/useMasterDataStatus';

const fmtDeadline = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

/**
 * Master-data completion gate.
 *  - PRE-deadline + incomplete → amber warning strip below the action bar.
 *  - POST-deadline + incomplete → non-dismissible modal forcing the user to
 *    finish their profile before any task work is possible.
 *
 * Super Admins (is_exempt) see nothing. The component renders inline so
 * Layout can place it directly under the sticky action strip.
 */
export default function MasterDataGate() {
  const { status } = useMasterDataStatus();
  const navigate = useNavigate();
  const location = useLocation();

  if (!status || status.is_exempt || status.is_complete) return null;

  const onProfilePage = location.pathname === '/my-profile';
  const fields = (status.missing_fields || []).map(f => f.label).join(', ');

  // Hard block: deadline passed
  if (status.should_block) {
    return (
      <>
        {/* Persistent banner so even if the modal is somehow bypassed,
            the user knows what's wrong. */}
        <div
          className="bg-red-600 text-white px-4 py-2 text-sm flex items-center gap-2"
          data-testid="master-data-block-banner"
        >
          <ShieldAlert className="h-4 w-4 flex-shrink-0" />
          <span className="font-semibold">Task actions are locked.</span>
          <span className="opacity-90 hidden sm:inline">
            Complete your HR Master Data to resume work.
          </span>
        </div>
        <Dialog open={!onProfilePage}>
          <DialogContent
            className="max-w-lg [&>button]:hidden"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            data-testid="master-data-block-modal"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <ShieldAlert className="h-5 w-5" />
                HR Master Data Required
              </DialogTitle>
              <DialogDescription>
                Per company policy, every employee must keep their HR Master
                Data up to date. The deadline of <strong>{fmtDeadline(status.deadline)}</strong> has
                passed, so task actions (Start, In Progress, Timer) are locked
                until you complete your profile.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
              <p className="font-semibold text-red-800 mb-1">
                Missing fields ({status.missing_fields.length})
              </p>
              <p className="text-red-700 text-xs leading-relaxed">{fields}</p>
            </div>
            <DialogFooter>
              <Button
                onClick={() => navigate('/my-profile')}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="master-data-go-to-profile-btn"
              >
                Update My Profile Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Soft warning: deadline still ahead
  if (status.should_warn && !onProfilePage) {
    return (
      <div
        className="bg-amber-500 text-white px-4 py-2 text-xs flex items-center justify-between gap-3 flex-wrap"
        data-testid="master-data-warning-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="font-semibold">Action required:</span>
          <span className="truncate">
            Complete your HR Master Data by <strong>{fmtDeadline(status.deadline)}</strong>.
            After the deadline, you won't be able to start any task.
          </span>
        </div>
        <Button
          size="sm"
          onClick={() => navigate('/my-profile')}
          className="bg-white text-amber-700 hover:bg-amber-100 h-7 text-xs"
          data-testid="master-data-warning-update-btn"
        >
          Update now
        </Button>
      </div>
    );
  }

  return null;
}
